import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    userProfileId: obj?.user_profile_id,
    employeeCode: obj?.employee_code,
    fullName: obj?.full_name,
    email: obj?.email,
    phone: obj?.phone,
    role: obj?.role,
    specializations: obj?.specializations,
    certifications: obj?.certifications,
    location: obj?.location,
    isActive: obj?.is_active,
    hourlyRate: obj?.hourly_rate,
    performanceScore: obj?.performance_score,
    totalAssignments: obj?.total_assignments,
    completedAssignments: obj?.completed_assignments,
    avatarUrl: obj?.avatar_url,
    metadata: obj?.metadata,
    createdBy: obj?.created_by,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
  };
};

const toCamelCaseSchedule = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    teamMemberId: obj?.team_member_id,
    assignmentTitle: obj?.assignment_title,
    description: obj?.description,
    serviceType: obj?.service_type,
    clientName: obj?.client_name,
    clientPhone: obj?.client_phone,
    location: obj?.location,
    startTime: obj?.start_time,
    endTime: obj?.end_time,
    status: obj?.status,
    priority: obj?.priority,
    requiredMaterials: obj?.required_materials,
    vehicleAssigned: obj?.vehicle_assigned,
    notes: obj?.notes,
    completionNotes: obj?.completion_notes,
    assignedBy: obj?.assigned_by,
    completedAt: obj?.completed_at,
    metadata: obj?.metadata,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
    teamMember: obj?.team_member ? toCamelCase(obj?.team_member) : null,
  };
};

const toCamelCaseAvailability = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    teamMemberId: obj?.team_member_id,
    date: obj?.date,
    isAvailable: obj?.is_available,
    reason: obj?.reason,
    notes: obj?.notes,
    createdAt: obj?.created_at,
  };
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  return {
    user_profile_id: obj?.userProfileId,
    employee_code: obj?.employeeCode,
    full_name: obj?.fullName,
    email: obj?.email,
    phone: obj?.phone,
    role: obj?.role,
    specializations: obj?.specializations,
    certifications: obj?.certifications,
    location: obj?.location,
    is_active: obj?.isActive,
    hourly_rate: obj?.hourlyRate,
    performance_score: obj?.performanceScore,
    avatar_url: obj?.avatarUrl,
    metadata: obj?.metadata,
  };
};

const toSnakeCaseSchedule = (obj) => {
  return {
    team_member_id: obj?.teamMemberId,
    assignment_title: obj?.assignmentTitle,
    description: obj?.description,
    service_type: obj?.serviceType,
    client_name: obj?.clientName,
    client_phone: obj?.clientPhone,
    location: obj?.location,
    start_time: obj?.startTime,
    end_time: obj?.endTime,
    status: obj?.status,
    priority: obj?.priority,
    required_materials: obj?.requiredMaterials,
    vehicle_assigned: obj?.vehicleAssigned,
    notes: obj?.notes,
    completion_notes: obj?.completionNotes,
  };
};

const toSnakeCaseAvailability = (obj) => {
  return {
    team_member_id: obj?.teamMemberId,
    date: obj?.date,
    is_available: obj?.isAvailable,
    reason: obj?.reason,
    notes: obj?.notes,
  };
};

export const teamService = {
  // Team Members
  async getAllMembers() {
    const { data, error } = await supabase
      ?.from('team_members')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getActiveMembers() {
    const { data, error } = await supabase
      ?.from('team_members')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('full_name', { ascending: true });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getMemberById(id) {
    const { data, error } = await supabase
      ?.from('team_members')
      ?.select('*')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getMembersByRole(role) {
    const { data, error } = await supabase
      ?.from('team_members')
      ?.select('*')
      ?.eq('role', role)
      ?.eq('is_active', true)
      ?.order('full_name', { ascending: true });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async createMember(member) {
    const { data, error } = await supabase
      ?.from('team_members')
      ?.insert(toSnakeCase(member))
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async updateMember(id, member) {
    const { data, error } = await supabase
      ?.from('team_members')
      ?.update(toSnakeCase(member))
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async deleteMember(id) {
    const { error } = await supabase?.from('team_members')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Team Schedules
  async getAllSchedules() {
    const { data, error } = await supabase
      ?.from('team_schedules')
      ?.select(`
        *,
        team_member:team_members(*)
      `)
      ?.order('start_time', { ascending: true });

    if (error) throw error;
    return data?.map(toCamelCaseSchedule) || [];
  },

  async getScheduleById(id) {
    const { data, error } = await supabase
      ?.from('team_schedules')
      ?.select(`
        *,
        team_member:team_members(*)
      `)
      ?.eq('id', id)
      ?.single();

    if (error) throw error;
    return toCamelCaseSchedule(data);
  },

  async getSchedulesByMember(memberId) {
    const { data, error } = await supabase
      ?.from('team_schedules')
      ?.select(`
        *,
        team_member:team_members(*)
      `)
      ?.eq('team_member_id', memberId)
      ?.order('start_time', { ascending: true });

    if (error) throw error;
    return data?.map(toCamelCaseSchedule) || [];
  },

  async getSchedulesByDateRange(startDate, endDate) {
    const { data, error } = await supabase
      ?.from('team_schedules')
      ?.select(`
        *,
        team_member:team_members(*)
      `)
      ?.gte('start_time', startDate)
      ?.lte('start_time', endDate)
      ?.order('start_time', { ascending: true });

    if (error) throw error;
    return data?.map(toCamelCaseSchedule) || [];
  },

  async createSchedule(schedule) {
    const { data: { user } } = await supabase?.auth?.getUser();

    const { data, error } = await supabase
      ?.from('team_schedules')
      ?.insert({
        ...toSnakeCaseSchedule(schedule),
        assigned_by: user?.id,
      })
      ?.select(`
        *,
        team_member:team_members(*)
      `)
      ?.single();

    if (error) throw error;
    return toCamelCaseSchedule(data);
  },

  async updateSchedule(id, schedule) {
    const { data, error } = await supabase
      ?.from('team_schedules')
      ?.update(toSnakeCaseSchedule(schedule))
      ?.eq('id', id)
      ?.select(`
        *,
        team_member:team_members(*)
      `)
      ?.single();

    if (error) throw error;
    return toCamelCaseSchedule(data);
  },

  async deleteSchedule(id) {
    const { error } = await supabase?.from('team_schedules')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Auto-assignment algorithm
  async findBestMemberForAssignment(assignmentData) {
    try {
      // Get all active members
      const members = await this.getActiveMembers();

      if (!members || members?.length === 0) {
        return null;
      }

      // Get schedules for the requested time period
      const startTime = new Date(assignmentData?.startTime);
      const endTime = new Date(assignmentData?.endTime);
      
      const schedules = await this.getSchedulesByDateRange(
        startTime?.toISOString(),
        endTime?.toISOString()
      );

      // Score each member
      const scoredMembers = members?.map(member => {
        let score = 0;

        // Check availability (not already scheduled)
        const isAvailable = !schedules?.some(s => 
          s?.teamMemberId === member?.id &&
          s?.status !== 'cancelled' &&
          new Date(s?.startTime) < endTime &&
          new Date(s?.endTime) > startTime
        );

        if (!isAvailable) {
          return { member, score: -1 }; // Not available
        }

        // Base score for being available
        score += 100;

        // Specialization match (if provided)
        if (assignmentData?.serviceType && member?.specializations?.includes(assignmentData?.serviceType)) {
          score += 50;
        }

        // Location proximity (if same location)
        if (assignmentData?.location && member?.location === assignmentData?.location) {
          score += 30;
        }

        // Performance score
        score += (member?.performanceScore || 0) * 10;

        // Workload balance (prefer members with fewer assignments)
        const workloadRatio = member?.totalAssignments > 0 
          ? member?.completedAssignments / member?.totalAssignments 
          : 1;
        score += workloadRatio * 20;

        // Penalize if member has many active assignments
        const activeAssignments = schedules?.filter(s => 
          s?.teamMemberId === member?.id && 
          (s?.status === 'assigned' || s?.status === 'in_progress')
        )?.length || 0;
        score -= activeAssignments * 10;

        return { member, score };
      });

      // Filter out unavailable members and sort by score
      const availableMembers = scoredMembers
        ?.filter(sm => sm?.score > 0)
        ?.sort((a, b) => b?.score - a?.score);

      // Return the best match
      return availableMembers?.[0]?.member || null;
    } catch (error) {
      console.error('Error finding best member:', error);
      throw error;
    }
  },

  // Team Availability
  async getAvailability(memberId, date) {
    const { data, error } = await supabase
      ?.from('team_availability')
      ?.select('*')
      ?.eq('team_member_id', memberId)
      ?.eq('date', date)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return data ? toCamelCaseAvailability(data) : null;
  },

  async setAvailability(availability) {
    const { data, error } = await supabase
      ?.from('team_availability')
      ?.upsert(toSnakeCaseAvailability(availability))
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCaseAvailability(data);
  },

  subscribeToSchedules(callback) {
    const channel = supabase
      ?.channel('team_schedules')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'team_schedules' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },
};